import { useScreenWidth } from './useScreenWidth';
import { unref } from 'vue';

/**
 * Resolves a ref to a focusable DOM element.
 * @param {unknown} ref - The ref to resolve.
 * @returns {HTMLElement | null} The element to focus, or null if not available.
 */
function resolveToFocusElement(ref: unknown): HTMLElement | null {
  if (!ref) return null;
  if (ref instanceof HTMLElement) return ref;

  const candidate = unref(ref);
  if (!candidate) return null;

  const el = (typeof candidate === 'object' && candidate !== null && '$el' in candidate)
    ? (candidate as { $el: unknown }).$el
    : candidate;

  return el instanceof HTMLElement ? el : null;
}

/**
 * Gets the menu button element from a component that exposes menuButtonRef
 * (e.g. UtilityBar in H5, HeaderNav in H6).
 */
function getMenuButtonFromComponent(parentRef: unknown): HTMLElement | null {
  const instance = unref(parentRef);
  if (!instance) return null;
  const menuRef = (instance as { menuButtonRef?: unknown }).menuButtonRef;
  return resolveToFocusElement(menuRef);
}

/**
* Resolves the appropriate menu button element to focus.
* @param {Record<string, unknown>} refs - The refs object containing menu button references.
* @param {string} headerVersion - The header version.
* @returns {HTMLElement | null} The element to focus, or null if not available.
*/
export function menuButtonFocus({ refs, headerVersion }: { refs: Record<string, unknown>; headerVersion: string }) {
  const { screenSize } = useScreenWidth();
  const { menuButtonRef, menuButtonMobileRef, utilityBarRef1, utilityBarRef2, headerNavRef, headerNavMobileRef } = refs;

  if (screenSize.value < 1024 && headerVersion === 'H2') {
    return resolveToFocusElement(menuButtonMobileRef);
  }
  if (headerVersion === 'H5') {
    return getMenuButtonFromComponent(utilityBarRef1 ?? utilityBarRef2);
  }
  if (headerVersion === 'H6') {
    return getMenuButtonFromComponent(headerNavRef ?? headerNavMobileRef);
  }

  return resolveToFocusElement(menuButtonRef);
}
